import { useMemo } from 'react';
import { useTheme, type ResolvedTheme } from './theme';

/**
 * Tema dos gráficos (recharts) a partir dos tokens. Os valores são var(--color-*), então o
 * mesmo objeto serve para o claro e o escuro (SVG aceita variáveis CSS em fill/stroke).
 * Para bibliotecas que precisam da cor resolvida (canvas, contas de cor), use `useChartTheme()`.
 *
 *   <CartesianGrid {...chartTheme.grid} />
 *   <XAxis dataKey="dia" {...chartTheme.axis} />
 *   <Tooltip {...chartTheme.tooltip} />
 *   <Bar dataKey="total" fill={chartTheme.colors[0]} radius={chartTheme.bar.radius} />
 */
export const CHART_SERIES = [
  'var(--color-chart-1)',
  'var(--color-chart-2)',
  'var(--color-chart-3)',
  'var(--color-chart-4)',
  'var(--color-chart-5)',
  'var(--color-chart-6)',
] as const;

export const chartTheme = {
  colors: CHART_SERIES,
  primary: 'var(--color-chart-1)',
  /** Fundo suave da série principal (barras "não destacadas"). */
  primarySoft: 'var(--color-primary-soft-2)',
  positive: 'var(--color-success)',
  negative: 'var(--color-danger)',
  neutral: 'var(--color-chart-6)',
  surface: 'var(--color-surface)',
  grid: { stroke: 'var(--color-line)', strokeDasharray: '3 4', vertical: false },
  axis: {
    stroke: 'var(--color-line)',
    tick: { fill: 'var(--color-ink-3)', fontSize: 12, fontFamily: 'var(--font-sans)' },
    tickLine: false,
    axisLine: false,
    tickMargin: 8,
  },
  tooltip: {
    contentStyle: {
      background: 'var(--color-elevated)',
      border: '1px solid var(--color-line)',
      borderRadius: 12,
      boxShadow: 'var(--elev-pop)',
      color: 'var(--color-ink)',
      fontSize: 13,
      padding: '8px 12px',
    },
    labelStyle: { color: 'var(--color-ink-2)', fontWeight: 500, marginBottom: 4 },
    itemStyle: { color: 'var(--color-ink)', padding: 0 },
    cursor: { fill: 'var(--color-ink)', fillOpacity: 0.05 },
    wrapperStyle: { outline: 'none' },
  },
  /** Cursor vertical para gráficos de linha/área. */
  cursorLine: { stroke: 'var(--color-line-strong)', strokeWidth: 1, strokeDasharray: '3 3' },
  legend: { wrapperStyle: { fontSize: 12, color: 'var(--color-ink-2)' }, iconType: 'circle' as const, iconSize: 8 },
  bar: { radius: [6, 6, 2, 2] as [number, number, number, number], maxBarSize: 44 },
  line: { strokeWidth: 2, dot: false, activeDot: { r: 4, strokeWidth: 2, stroke: 'var(--color-surface)' } },
  area: { fillOpacity: 0.16 },
} as const;

/** Cor da série `i` (volta ao início depois da sexta). */
export function chartColor(i: number): string {
  return CHART_SERIES[((i % CHART_SERIES.length) + CHART_SERIES.length) % CHART_SERIES.length]!;
}

/** Lê o valor atual de uma variável CSS do tema (ex.: '--color-chart-1' → '#6b4eff'). */
export function resolveCssVar(name: string, el: Element = document.documentElement): string {
  return getComputedStyle(el).getPropertyValue(name).trim();
}

export interface ResolvedChartTheme {
  theme: ResolvedTheme;
  colors: string[];
  grid: string;
  axis: string;
  surface: string;
  ink: string;
  positive: string;
  negative: string;
}

/** Cores resolvidas (hex/rgb) do tema atual. Recalcula quando o tema muda. */
export function useChartTheme(): ResolvedChartTheme {
  const { resolvedTheme } = useTheme();
  return useMemo(() => {
    if (typeof document === 'undefined') {
      return { theme: resolvedTheme, colors: [], grid: '', axis: '', surface: '', ink: '', positive: '', negative: '' };
    }
    const v = (n: string) => resolveCssVar(n);
    return {
      theme: resolvedTheme,
      colors: [1, 2, 3, 4, 5, 6].map((i) => v(`--color-chart-${i}`)),
      grid: v('--color-line'),
      axis: v('--color-ink-3'),
      surface: v('--color-surface'),
      ink: v('--color-ink'),
      positive: v('--color-success'),
      negative: v('--color-danger'),
    };
  }, [resolvedTheme]);
}
